// src/pages/admin/AdminServices.js
// Manage town service listings — add, edit, toggle featured

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db, storage } from '../../lib/firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import {
  collection, addDoc, getDocs, updateDoc, deleteDoc,
  doc, query, where, serverTimestamp
} from 'firebase/firestore';
import { approveService, rejectService, getAllServiceClaims, approveServiceClaim, rejectServiceClaim } from '../../lib/db';
import { useAuth } from '../../lib/AuthContext';
import toast from 'react-hot-toast';
import { PageBack } from '../../components/shared/PageBack';

const EMPTY = {
  name: '', category: 'health', description: '',
  phone: '', email: '', website: '', hours: '',
  address: '', tier: 'free', emoji: '🏥', logoUrl: '',
  townId: 'loughrea', active: true,
};

const CATEGORIES = [
  { value: 'health',          label: 'Health & Medical' },
  { value: 'legal_financial', label: 'Legal & Financial' },
  { value: 'food_drink',      label: 'Food & Drink' },
  { value: 'accommodation',   label: 'Accommodation' },
  { value: 'beauty',          label: 'Beauty & Hair' },
  { value: 'sport_leisure',   label: 'Sport & Leisure' },
  { value: 'civic',           label: 'Civic & Community' },
  { value: 'worship',         label: 'Worship' },
  { value: 'education',       label: 'Education' },
  { value: 'transport',       label: 'Transport' },
  { value: 'home_trade',      label: 'Home & Trade' },
  { value: 'shopping',        label: 'Shops & Retail' },
  { value: 'services',        label: 'Other Services' },
];
const TIERS = [
  { value: 'free', label: 'Free listing' },
  { value: 'standard', label: 'Standard (€15/mo)' },
  { value: 'featured', label: 'Featured (€29/mo)' },
];

const AdminServices = () => {
  const navigate = useNavigate();
  const { isTownAdmin, townAdminScope, user } = useAuth();
  const [services, setServices] = useState([]);
  const [towns, setTowns] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [selectedTown, setSelectedTown] = useState(isTownAdmin ? townAdminScope : 'loughrea');
  const [claims, setClaims] = useState([]);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  useEffect(() => { loadAll(); loadClaims(); }, [selectedTown]);

  const loadClaims = async () => {
    const all = await getAllServiceClaims();
    setClaims(all.filter(c => c.status === 'pending'));
  };

  const loadAll = async () => {
    const townsSnap = await getDocs(collection(db, 'towns'));
    const ts = townsSnap.docs.map(d => ({ id: d.id, ...d.data() }));
    setTowns(ts);

    const q = query(collection(db, 'services'), where('townId', '==', selectedTown));
    const snap = await getDocs(q);
    setServices(snap.docs.map(d => ({ id: d.id, ...d.data() })));
  };

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error('Logo must be under 2MB'); return; }
    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
  };

  const handleSave = async () => {
    if (!form.name) { toast.error('Name is required'); return; }
    setSaving(true);
    try {
      let logoUrl = form.logoUrl;
      if (logoFile) {
        setUploadingLogo(true);
        const logoRef = ref(storage, `service-logos/${user.uid}/${Date.now()}`);
        await uploadBytes(logoRef, logoFile);
        logoUrl = await getDownloadURL(logoRef);
        setUploadingLogo(false);
      }
      if (editingId) {
        // Editing an existing listing — never touch claimedBy/claimStatus/
        // townId/createdAt from here, same boundary the owner-edit path
        // respects. Admin/Town Admin editing content isn't the same thing
        // as approving a claim.
        const { claimedBy, claimStatus, townId, createdAt, ...editable } = form;
        await updateDoc(doc(db, 'services', editingId), { ...editable, logoUrl, updatedAt: serverTimestamp() });
        toast.success('Listing updated!');
      } else {
        await addDoc(collection(db, 'services'), {
          ...form,
          logoUrl,
          townId: selectedTown,
          status: 'published',
          claimedBy: null,
          claimStatus: 'unclaimed',
          createdAt: serverTimestamp(),
        });
        toast.success('Service added!');
      }
      setForm(EMPTY);
      setLogoFile(null);
      setLogoPreview(null);
      setAdding(false);
      setEditingId(null);
      loadAll();
    } catch (err) {
      console.error(err);
      toast.error('Failed to save');
    }
    setSaving(false);
  };

  const startEdit = (svc) => {
    setForm({
      name: svc.name || '', category: svc.category || 'health', description: svc.description || '',
      phone: svc.phone || '', email: svc.email || '', website: svc.website || '', hours: svc.hours || '',
      address: svc.address || '', tier: svc.tier || 'free', emoji: svc.emoji || '🏥', logoUrl: svc.logoUrl || '',
      townId: svc.townId, active: svc.active !== false,
    });
    setLogoFile(null);
    setLogoPreview(svc.logoUrl || null);
    setEditingId(svc.id);
    setAdding(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const cancelForm = () => {
    setForm(EMPTY);
    setLogoFile(null);
    setLogoPreview(null);
    setAdding(false);
    setEditingId(null);
  };

  const toggleActive = async (id, current) => {
    await updateDoc(doc(db, 'services', id), { active: !current });
    loadAll();
    toast.success(current ? 'Listing hidden' : 'Listing visible');
  };

  const setTier = async (id, tier) => {
    await updateDoc(doc(db, 'services', id), { tier });
    loadAll();
    toast.success('Tier updated');
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete "${name}"?`)) return;
    await deleteDoc(doc(db, 'services', id));
    loadAll();
    toast.success('Deleted');
  };

  const handleApprove = async (id) => {
    await approveService(id);
    loadAll();
    toast.success('Approved — back on the directory');
  };

  const handleReject = async (id) => {
    await rejectService(id);
    loadAll();
    toast.success('Declined — hidden from public view');
  };

  const handleApproveClaim = async (claim) => {
    await approveServiceClaim(claim.id, claim.serviceId, claim.requestedBy, {
      contactEmail: claim.contactEmail, contactPhone: claim.contactPhone, website: claim.website,
    });
    toast.success(`Ownership handed to the claimant for "${claim.serviceName}" — their contact details are now on the listing`);
    loadClaims();
    loadAll();
  };

  const handleRejectClaim = async (claimId) => {
    await rejectServiceClaim(claimId);
    toast.success('Claim declined');
    loadClaims();
  };

  const pendingCount = services.filter(s => s.status === 'pending_review').length;
  const sortedServices = [...services].sort((a, b) => {
    const aPending = a.status === 'pending_review' ? 0 : 1;
    const bPending = b.status === 'pending_review' ? 0 : 1;
    return aPending - bPending;
  });

  return (
    <div style={{ background: 'var(--cream)', minHeight: '100vh', padding: '52px 20px 40px' }}>
      <button onClick={() => navigate('/admin')} style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'none', border: 'none', cursor: 'pointer', marginBottom: 20 }}>
        <div style={{ width: 34, height: 34, background: 'var(--white)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15, color: 'var(--sage-dark)', boxShadow: 'var(--shadow-sm)' }}>←</div>
        <span style={{ fontSize: 13, color: 'var(--muted)', fontWeight: 600 }}>Admin</span>
      </button>

      <div style={{ fontSize: 26, fontWeight: 800, color: 'var(--ink)', marginBottom: 16 }}>🏘️ Town Services</div>

      {pendingCount > 0 && (
        <div style={{ background: '#FFF3CD', color: '#8A6D1D', fontSize: 13, fontWeight: 700, borderRadius: 14, padding: '12px 16px', marginBottom: 16 }}>
          🔔 {pendingCount} submission{pendingCount !== 1 ? 's' : ''} waiting for review
        </div>
      )}

      {claims.length > 0 && (
        <div style={{ background: 'var(--white)', borderRadius: 16, padding: 16, marginBottom: 16, border: '1.5px solid #FFE39A' }}>
          <div style={{ fontSize: 14, fontWeight: 800, color: 'var(--ink)', marginBottom: 10 }}>
            🏢 Business claims waiting for review ({claims.length})
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {claims.map(claim => (
              <div key={claim.id} style={{ background: 'var(--cream)', borderRadius: 12, padding: 12 }}>
                <div style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--ink)' }}>{claim.serviceName}</div>
                <div style={{ fontSize: 12, color: 'var(--muted)', fontWeight: 500, marginTop: 2 }}>
                  Contact: {claim.contactEmail}{claim.contactPhone ? ` · ${claim.contactPhone}` : ''}
                </div>
                {claim.website && (
                  <div style={{ fontSize: 12, color: 'var(--muted)', fontWeight: 500 }}>🔗 {claim.website}</div>
                )}
                {claim.message && (
                  <div style={{ fontSize: 12, color: 'var(--muted)', fontStyle: 'italic', marginTop: 4 }}>"{claim.message}"</div>
                )}
                <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                  <button onClick={() => handleApproveClaim(claim)}
                    style={{ flex: 1, background: 'var(--sage-deep)', color: 'white', border: 'none', borderRadius: 10, padding: '8px 10px', fontSize: 12, fontWeight: 700, fontFamily: 'Nunito', cursor: 'pointer' }}>
                    Approve — hand over listing
                  </button>
                  <button onClick={() => handleRejectClaim(claim.id)}
                    style={{ flex: 1, background: '#FBEAE7', color: '#C0392B', border: 'none', borderRadius: 10, padding: '8px 10px', fontSize: 12, fontWeight: 700, fontFamily: 'Nunito', cursor: 'pointer' }}>
                    Decline
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Town selector — locked to their own town for a Town Admin */}
      {isTownAdmin ? (
        <div style={{ background: 'var(--sage-pale)', color: 'var(--sage-dark)', fontWeight: 700, fontSize: 13, borderRadius: 12, padding: '10px 14px', marginBottom: 16 }}>
          📍 Managing: {towns.find(t => t.id === selectedTown)?.name || selectedTown}
        </div>
      ) : (
        <select
          className="form-input"
          style={{ marginBottom: 16 }}
          value={selectedTown}
          onChange={e => setSelectedTown(e.target.value)}
        >
          {towns.map(t => (
            <option key={t.id} value={t.id}>{t.name}</option>
          ))}
        </select>
      )}

      {/* Add button */}
      <button
        onClick={() => (adding ? cancelForm() : setAdding(true))}
        style={{ width: '100%', background: 'var(--sage-deep)', color: 'white', border: 'none', borderRadius: 16, padding: 15, fontSize: 15, fontWeight: 700, fontFamily: 'Nunito', cursor: 'pointer', marginBottom: 16 }}
      >
        {adding ? '✕ Cancel' : '+ Add service listing'}
      </button>

      {/* Add / edit form */}
      {adding && (
        <div style={{ background: 'var(--white)', borderRadius: 18, padding: 18, marginBottom: 16, boxShadow: 'var(--shadow-sm)' }}>
          <div style={{ fontSize: 15, fontWeight: 800, color: 'var(--ink)', marginBottom: 4 }}>
            {editingId ? '✏️ Edit listing' : '+ New listing'}
          </div>
          {editingId && (
            <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 12 }}>
              Editing an existing listing — changes go live immediately, no re-approval needed.
            </div>
          )}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {[
              ['Name *', 'name', 'e.g. Loughrea Medical Centre'],
              ['Emoji', 'emoji', 'e.g. 🏥'],
              ['Phone', 'phone', 'e.g. 091 841 234'],
              ['Email', 'email', 'e.g. info@example.ie'],
              ['Website', 'website', 'https://...'],
              ['Opening hours', 'hours', 'e.g. Mon–Fri 9am–5pm'],
              ['Address', 'address', 'Full address'],
            ].map(([lbl, key, ph]) => (
              <div key={key}>
                <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1.2px', color: 'var(--muted)', marginBottom: 5 }}>{lbl}</div>
                <input className="form-input" value={form[key]} onChange={e => set(key, e.target.value)} placeholder={ph} />
              </div>
            ))}
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1.2px', color: 'var(--muted)', marginBottom: 5 }}>Logo</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{
                  width: 56, height: 56, borderRadius: 12, background: 'var(--sage-pale)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', flexShrink: 0,
                }}>
                  {logoPreview ? (
                    <img src={logoPreview} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  ) : (
                    <span style={{ fontSize: 22 }}>{form.emoji || '🏘️'}</span>
                  )}
                </div>
                <label style={{ padding: '10px 16px', borderRadius: 12, border: '1.5px solid var(--sage-light)', background: 'var(--white)', color: 'var(--sage-dark)', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'Nunito, sans-serif' }}>
                  {uploadingLogo ? 'Uploading...' : logoFile || logoPreview ? 'Change logo' : 'Upload logo'}
                  <input type="file" accept="image/*" onChange={handleLogoChange} style={{ display: 'none' }} disabled={uploadingLogo} />
                </label>
              </div>
            </div>
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1.2px', color: 'var(--muted)', marginBottom: 5 }}>Description</div>
              <textarea className="form-input" value={form.description} onChange={e => set('description', e.target.value)} placeholder="Brief description" rows={2} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1.2px', color: 'var(--muted)', marginBottom: 5 }}>Category</div>
                <select className="form-input" value={form.category} onChange={e => set('category', e.target.value)}>
                  {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </div>
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1.2px', color: 'var(--muted)', marginBottom: 5 }}>Tier</div>
                <select className="form-input" value={form.tier} onChange={e => set('tier', e.target.value)}>
                  {TIERS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
            </div>
            <button onClick={handleSave} style={{ background: 'var(--sage-deep)', color: 'white', border: 'none', borderRadius: 14, padding: 15, fontSize: 15, fontWeight: 700, fontFamily: 'Nunito', cursor: 'pointer', opacity: (saving || uploadingLogo) ? 0.7 : 1 }} disabled={saving || uploadingLogo}>
              {uploadingLogo ? 'Uploading logo...' : saving ? 'Saving...' : editingId ? 'Save changes' : 'Save listing'}
            </button>
          </div>
        </div>
      )}

      {/* Services list */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {sortedServices.map(svc => {
          const isPending = svc.status === 'pending_review';
          return (
          <div key={svc.id} style={{ background: 'var(--white)', borderRadius: 16, padding: '14px 16px', boxShadow: 'var(--shadow-sm)', opacity: svc.active === false && !isPending ? 0.5 : 1, border: isPending ? '1.5px solid #FFE39A' : 'none' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 15, fontWeight: 700, color: 'var(--ink)' }}>
                {svc.logoUrl ? (
                  <img src={svc.logoUrl} alt="" style={{ width: 28, height: 28, borderRadius: 8, objectFit: 'cover' }}
                    onError={e => { e.target.style.display = 'none'; }} />
                ) : (
                  <span>{svc.emoji}</span>
                )}
                <span>{svc.name}</span>
              </div>
              <span style={{ fontSize: 10, background: isPending ? '#FFF3CD' : (svc.tier === 'featured' ? 'var(--warm-pale)' : 'var(--sage-pale)'), color: isPending ? '#8A6D1D' : (svc.tier === 'featured' ? 'var(--warm-dark)' : 'var(--sage-deep)'), padding: '2px 8px', borderRadius: 100, fontWeight: 700 }}>
                {isPending ? 'Pending review' : svc.tier}
              </span>
            </div>
            <div style={{ fontSize: 12, color: 'var(--muted)', fontWeight: 500, marginBottom: 10 }}>
              {CATEGORIES.find(c => c.value === svc.category)?.label || svc.category} · {svc.phone || 'No phone'}
            </div>
            {isPending && svc.pendingReason && (
              <div style={{ fontSize: 11.5, color: '#8A6D1D', fontWeight: 600, marginBottom: 10, background: '#FFF9EB', borderRadius: 8, padding: '6px 10px' }}>
                Flagged: {svc.pendingReason === 'keyword_flagged' ? `keyword match (${svc.flaggedKeyword || 'n/a'})`
                  : svc.pendingReason === 'profanity_flagged' ? 'profanity filter'
                  : svc.pendingReason === 'ai_flagged' ? (svc.aiModerationNote || 'AI review flag')
                  : svc.pendingReason}
              </div>
            )}
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {isPending && (
                <>
                  <button onClick={() => handleApprove(svc.id)}
                    style={{ flex: 1, minWidth: 80, background: 'var(--sage-deep)', color: 'white', border: 'none', borderRadius: 10, padding: '7px 10px', fontSize: 12, fontWeight: 700, fontFamily: 'Nunito', cursor: 'pointer' }}>
                    Approve
                  </button>
                  <button onClick={() => handleReject(svc.id)}
                    style={{ flex: 1, minWidth: 80, background: '#FBEAE7', color: '#C0392B', border: 'none', borderRadius: 10, padding: '7px 10px', fontSize: 12, fontWeight: 700, fontFamily: 'Nunito', cursor: 'pointer' }}>
                    Decline
                  </button>
                </>
              )}
              <button onClick={() => toggleActive(svc.id, svc.active !== false)}
                style={{ flex: 1, background: svc.active === false ? 'var(--sage-pale)' : '#F0F0EE', color: 'var(--ink)', border: 'none', borderRadius: 10, padding: '7px 10px', fontSize: 12, fontWeight: 700, fontFamily: 'Nunito', cursor: 'pointer' }}>
                {svc.active === false ? 'Show' : 'Hide'}
              </button>
              <button onClick={() => startEdit(svc)}
                style={{ flex: 1, background: 'var(--sage-pale)', color: 'var(--sage-dark)', border: 'none', borderRadius: 10, padding: '7px 10px', fontSize: 12, fontWeight: 700, fontFamily: 'Nunito', cursor: 'pointer' }}>
                Edit
              </button>
              <select
                value={svc.tier}
                onChange={e => setTier(svc.id, e.target.value)}
                style={{ flex: 2, borderRadius: 10, border: '1.5px solid var(--border)', padding: '7px 10px', fontSize: 12, fontFamily: 'Nunito', fontWeight: 600, background: 'var(--white)', cursor: 'pointer' }}
              >
                {TIERS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
              <button onClick={() => handleDelete(svc.id, svc.name)}
                style={{ background: 'var(--error-pale)', color: 'var(--error)', border: 'none', borderRadius: 10, padding: '7px 12px', fontSize: 12, fontWeight: 700, fontFamily: 'Nunito', cursor: 'pointer' }}>
                ✕
              </button>
            </div>
          </div>
        );})}
      </div>
      <PageBack position="bottom" fallbackPath="/admin" />
    </div>
  );
};

export default AdminServices;
